import { useEffect, useState } from "react";
import axios from "axios";
import ClientLayout from "@/components/ClientLayout";
import { useRestaurant } from "@/context/RestaurantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { TrendingUp, ShoppingBag, Package, Euro } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState("12");
  const { selected: restaurant } = useRestaurant();

  useEffect(() => {
    if (!restaurant) {
      setStats(null);
      setLoading(false);
      return;
    }
    fetchStats();
  }, [months, restaurant?.restaurant_id]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/stats/annual`, {
        params: {
          months: parseInt(months),
          restaurant_id: restaurant?.restaurant_id || "",
        },
      });
      setStats(response.data);
    } catch {
      toast.error("Error al cargar las estadísticas");
    } finally {
      setLoading(false);
    }
  };

  const canSeePrices = restaurant?.show_prices_to_client !== false;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const totalQuantity = (stats?.products || []).reduce(
    (sum, product) => sum + (product.total_quantity || 0),
    0,
  );
  const maxMetric = canSeePrices
    ? stats?.products?.[0]?.total_spent || 1
    : Math.max(...(stats?.products || []).map((product) => product.total_quantity || 0), 1);

  return (
    <ClientLayout>
      <div className="space-y-6" data-testid="client-stats">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{canSeePrices ? "Mi Gasto Anual" : "Mi Actividad"}</h1>
              <p className="text-slate-500">{canSeePrices ? "Resumen de tus compras" : "Resumen por cantidades pedidas"}</p>
              {restaurant && (
                <p className="text-sm text-blue-700 mt-2" data-testid="stats-current-restaurant">
                  Restaurante activo: {restaurant.name}
                </p>
              )}
            </div>
          </div>

          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-[180px]" data-testid="period-select">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Últimos 3 meses</SelectItem>
              <SelectItem value="6">Últimos 6 meses</SelectItem>
              <SelectItem value="12">Últimos 12 meses</SelectItem>
              <SelectItem value="24">Últimos 2 años</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <>
            <Card className="bg-gradient-to-br from-blue-600 to-blue-800 text-white overflow-hidden">
              <CardContent className="p-8">
                <div className="flex flex-col items-center text-center">
                  {canSeePrices ? <Euro className="w-12 h-12 mb-4 opacity-80" /> : <Package className="w-12 h-12 mb-4 opacity-80" />}
                  <p className="text-blue-100 text-lg mb-2">
                    {canSeePrices ? `Gasto Total (${months} meses)` : `Unidades pedidas (${months} meses)`}
                  </p>
                  <p className="text-5xl sm:text-6xl font-bold tracking-tight" data-testid="stats-main-metric">
                    {canSeePrices ? formatCurrency(stats?.total_spent || 0) : totalQuantity}
                  </p>
                  <p className="text-blue-200 mt-4 text-sm">
                    {canSeePrices ? "* Precios sin IVA" : "Distribución basada en cantidad pedida"}
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <ShoppingBag className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_orders || 0}</p>
                  <p className="text-sm text-slate-500">Pedidos</p>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4 text-center">
                  <Package className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-slate-900">{stats?.products?.length || 0}</p>
                  <p className="text-sm text-slate-500">Productos</p>
                </CardContent>
              </Card>
              {!canSeePrices && (
                <Card className="bg-white shadow-sm col-span-2 lg:col-span-1">
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="w-8 h-8 text-violet-600 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-slate-900">{totalQuantity}</p>
                    <p className="text-sm text-slate-500">Unidades</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {stats?.products?.length > 0 && (
              <Card className="bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {canSeePrices ? "Gasto por Producto" : "Cantidad por Producto"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {stats.products.map((product, index) => {
                      const quantityPercentage = totalQuantity
                        ? ((product.total_quantity / totalQuantity) * 100).toFixed(1)
                        : "0.0";
                      const barWidth = canSeePrices
                        ? ((product.total_spent || 0) / maxMetric) * 100
                        : ((product.total_quantity || 0) / maxMetric) * 100;

                      return (
                        <div key={product.product_id} className="space-y-2" data-testid={`product-bar-${index}`}>
                          <div className="flex items-center justify-between text-sm gap-4">
                            <span className="font-medium text-slate-900 truncate max-w-[60%]">
                              {product.product_name}
                            </span>
                            <span className="font-semibold text-slate-700">
                              {canSeePrices
                                ? formatCurrency(product.total_spent)
                                : `${product.total_quantity} ${product.unit}`}
                            </span>
                          </div>
                          <div className="relative h-8 bg-slate-100 rounded-lg overflow-hidden">
                            <div
                              className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg transition-all duration-500"
                              style={{ width: `${barWidth}%` }}
                            />
                            <div className="absolute inset-0 flex items-center px-3">
                              <span className="text-xs font-medium text-white drop-shadow-sm">
                                {canSeePrices ? product.percentage : quantityPercentage}%
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between text-xs text-slate-500">
                            <span>{product.total_quantity} {product.unit}</span>
                            <span>
                              {canSeePrices ? `Precio medio: ${formatCurrency(product.average_price)}` : `Porcentaje por cantidad: ${quantityPercentage}%`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {(!stats?.products || stats.products.length === 0) && (
              <Card className="bg-white shadow-sm">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Sin datos en este período</h3>
                  <p className="text-slate-500">No tienes pedidos en los últimos {months} meses</p>
                </CardContent>
              </Card>
            )}

            {stats?.products?.length > 0 && (
              <Card className="bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">{canSeePrices ? "Detalle por Producto" : "Detalle por Cantidad"}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="stats-detail-table">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-2 font-semibold text-slate-700">Producto</th>
                          <th className="text-right py-3 px-2 font-semibold text-slate-700">Cantidad</th>
                          {canSeePrices ? (
                            <>
                              <th className="text-right py-3 px-2 font-semibold text-slate-700 hidden sm:table-cell">Precio Medio</th>
                              <th className="text-right py-3 px-2 font-semibold text-slate-700">Total</th>
                            </>
                          ) : (
                            <th className="text-right py-3 px-2 font-semibold text-slate-700">% Cantidad</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {stats.products.map((product) => {
                          const quantityPercentage = totalQuantity
                            ? ((product.total_quantity / totalQuantity) * 100).toFixed(1)
                            : "0.0";

                          return (
                            <tr key={product.product_id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-2">
                                <span className="font-medium text-slate-900">{product.product_name}</span>
                              </td>
                              <td className="py-3 px-2 text-right text-slate-600">
                                {product.total_quantity} {product.unit}
                              </td>
                              {canSeePrices ? (
                                <>
                                  <td className="py-3 px-2 text-right text-slate-600 hidden sm:table-cell">
                                    {formatCurrency(product.average_price)}
                                  </td>
                                  <td className="py-3 px-2 text-right font-semibold text-slate-900">
                                    {formatCurrency(product.total_spent)}
                                  </td>
                                </>
                              ) : (
                                <td className="py-3 px-2 text-right font-semibold text-slate-900">{quantityPercentage}%</td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-50">
                          <td className="py-3 px-2 font-bold text-slate-900">TOTAL</td>
                          <td className="py-3 px-2 text-right font-bold text-slate-900">{totalQuantity}</td>
                          {canSeePrices ? (
                            <>
                              <td className="py-3 px-2" />
                              <td className="py-3 px-2 text-right font-bold text-blue-600 text-lg">
                                {formatCurrency(stats.total_spent)}
                              </td>
                            </>
                          ) : (
                            <td className="py-3 px-2 text-right font-bold text-blue-600 text-lg">100%</td>
                          )}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {canSeePrices && <p className="text-xs text-slate-400 mt-4 text-right">* Precios sin IVA</p>}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </ClientLayout>
  );
}
